import { useRef, useState } from "react";
import { Upload, Eye, Download, Trash2, ChevronDown, CheckCircle2, AlertCircle, Circle, PenLine, Loader2 } from "lucide-react";
import { DOK_KATEGORIEN } from "@/lib/dokumentKategorien";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "");

const STATUS = {
  ok: { ring: "border-emerald-300 bg-emerald-50/60", pill: "bg-emerald-600 text-white", Icon: CheckCircle2, text: "Vorhanden" },
  fehlt: { ring: "border-red-300 bg-red-50/60", pill: "bg-red-600 text-white", Icon: AlertCircle, text: "Fehlt" },
  leer: { ring: "border-stone-200 bg-white", pill: "bg-stone-200 text-stone-600", Icon: Circle, text: "Noch keine" },
};

const DateiZeile = ({ a, onOpen, onDownload, onDelete }) => (
  <div className="flex items-center gap-2 py-1.5 border-t border-stone-200/70 first:border-t-0" data-testid={`dok-datei-${a.id}`}>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-medium text-stone-800 truncate" title={a.filename}>{a.filename}</p>
      <p className="text-[11px] text-stone-500 flex items-center gap-1">
        {fmt(a.uploaded_at)}
        {a.signed && <span className="inline-flex items-center gap-0.5 text-emerald-700"><PenLine size={10} /> unterschrieben</span>}
      </p>
    </div>
    <button onClick={() => onOpen(a)} className="p-1 rounded hover:bg-stone-200/70 text-stone-600" title="Öffnen" data-testid={`dok-open-${a.id}`}><Eye size={14} /></button>
    <button onClick={() => onDownload(a)} className="p-1 rounded hover:bg-stone-200/70 text-stone-600" title="Download" data-testid={`dok-download-${a.id}`}><Download size={14} /></button>
    <button onClick={() => onDelete(a.id)} className="p-1 rounded hover:bg-red-100 text-red-500" title="Löschen" data-testid={`dok-delete-${a.id}`}><Trash2 size={14} /></button>
  </div>
);

export const DokumentKachel = ({ code, dateien, pflicht, uploading, onUpload, ...aktionen }) => {
  const input = useRef(null);
  const [offen, setOffen] = useState(false);
  const [drag, setDrag] = useState(false);
  const st = STATUS[dateien.length ? "ok" : pflicht ? "fehlt" : "leer"];
  const sortiert = [...dateien].sort((x, y) => String(y.uploaded_at || "").localeCompare(String(x.uploaded_at || "")));
  const sichtbar = offen ? sortiert : sortiert.slice(0, 1);
  const datei = (f) => f && onUpload(f, code);
  return (
    <div
      className={`rounded-xl border-2 p-3 flex flex-col gap-2 transition-colors duration-150 ${st.ring} ${drag ? "ring-2 ring-[#D9775A]" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); datei(e.dataTransfer.files?.[0]); }}
      data-testid={`dok-kachel-${code}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-stone-900 leading-tight">{DOK_KATEGORIEN[code].label}</p>
          <p className="text-[11px] text-stone-500">{pflicht ? "Pflichtunterlage" : "optional"}</p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${st.pill}`} data-testid={`dok-status-${code}`}>
          <st.Icon size={11} /> {st.text}{dateien.length > 1 ? ` · ${dateien.length}` : ""}
        </span>
      </div>
      {sichtbar.length > 0 && (
        <div className="rounded-lg bg-white/80 border border-stone-200/70 px-2">
          {sichtbar.map((a) => <DateiZeile key={a.id} a={a} {...aktionen} />)}
        </div>
      )}
      <div className="flex items-center gap-2 mt-auto">
        <input ref={input} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          onChange={(e) => { datei(e.target.files?.[0]); e.target.value = ""; }} data-testid={`dok-input-${code}`} />
        <button onClick={() => input.current?.click()} disabled={uploading}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-stone-900 text-white text-xs font-medium py-1.5 hover:bg-[#D9775A] transition-colors duration-150 disabled:opacity-50"
          data-testid={`dok-upload-${code}`}>
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Hochladen
        </button>
        {dateien.length > 1 && (
          <button onClick={() => setOffen(!offen)} className="inline-flex items-center gap-0.5 text-xs text-stone-600 hover:text-stone-900 px-1" data-testid={`dok-toggle-${code}`}>
            {offen ? "weniger" : `alle ${dateien.length}`} <ChevronDown size={13} className={`transition-transform ${offen ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
    </div>
  );
};
